import classNames from 'classnames';
import { Children, isValidElement, type FC, type ReactNode, type Ref } from 'react';

export const HOME_HORIZONTAL_SCROLL_CONTAINER_CLASS =
  'block w-full max-w-full min-w-0 overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch] [contain:inline-size] no-scrollbar pb-0.5';

export type HomeHorizontalScrollLayout = 'single-row' | 'two-row-grid';
export type HomeHorizontalScrollMobileMode = 'touch' | 'buttons';

export interface HomeHorizontalScrollProps {
  children: ReactNode;
  layout?: HomeHorizontalScrollLayout;
  itemClassName?: string;
  showEdgePadding?: boolean;
  trailingContent?: ReactNode;
  className?: string;
  contentClassName?: string;
  scrollRef?: Ref<HTMLDivElement>;
  mobileScrollMode?: HomeHorizontalScrollMobileMode;
}

const HomeHorizontalScroll: FC<HomeHorizontalScrollProps> = ({
  children,
  layout = 'single-row',
  itemClassName,
  showEdgePadding = false,
  trailingContent,
  className,
  contentClassName,
  scrollRef,
  mobileScrollMode = 'touch',
}) => {
  const items = Children.toArray(children).filter(isValidElement);

  return (
    <div
      ref={scrollRef}
      className={classNames(
        HOME_HORIZONTAL_SCROLL_CONTAINER_CLASS,
        'overflow-x-auto',
        mobileScrollMode === 'buttons' && 'max-md:scroll-smooth',
        className
      )}
    >
      <div
        className={classNames(
          'w-max',
          layout === 'two-row-grid'
            ? 'flex flex-nowrap gap-3 md:grid md:grid-flow-col md:grid-rows-2 md:justify-start md:gap-x-5 md:gap-y-5 md:auto-cols-[320px]'
            : 'flex items-stretch gap-2 md:gap-3',
          contentClassName
        )}
      >
        {showEdgePadding && <div aria-hidden className="w-2 shrink-0 md:hidden" />}
        {items.map((child, index) => (
          <div
            key={child.key ?? index}
            className={classNames(
              layout === 'two-row-grid' ? 'w-[280px] shrink-0 md:w-[320px] md:shrink-0' : 'shrink-0',
              itemClassName
            )}
          >
            {child}
          </div>
        ))}
        {trailingContent}
        {showEdgePadding && <div aria-hidden className="w-2 shrink-0 md:hidden" />}
      </div>
    </div>
  );
};

export default HomeHorizontalScroll;
